import React, { useState, useEffect } from "react";
import axios from "axios";
import PageHeader from "../components/ui/PageHeader";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import FormField from "../components/ui/FormField";
import DataTable from "../components/ui/DataTable";
import useAuth from "../hooks/useAuth";
import { useSeason } from "../context/SeasonContext";
import { FiRefreshCw } from "react-icons/fi";

export default function Companies() {
  const { refreshToken } = useAuth();
  const { selectedSeason } = useSeason();
  const [companies, setCompanies] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  const token = sessionStorage.getItem("maaze_token");
  const API = "https://maize.norissolutions.in/Php/public/api";

  const [form, setForm] = useState({
    season_id: selectedSeason || "",
    company_name: "",
    contact_person: "",
    mobile: "",
    email: "",
    address: "",
    city: "",
    district: "",
    state: "",
    pin_code: "",
    gst_no: "",
    bank_account: "",
    ifsc_code: ""
  });

  /* ================= FETCH DATA ================= */

  const fetchCompanies = async () => {
    try {
      const res = await axios.get(`${API}/companies`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { season_id: selectedSeason }
      });
      // Handle different API response structures
      // The network response shows data is in res.data.data.data
      const data = res.data?.data?.data || res.data?.data || res.data;
      setCompanies(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching companies:", err);
      setCompanies([]);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, [selectedSeason]);

  useEffect(() => {
    setForm(prev => ({ ...prev, season_id: selectedSeason || "" }));
  }, [selectedSeason]);

  /* ================= SAVE COMPANY ================= */

  const handleSubmit = async () => {
    try {
      const payload = { ...form };
      
      if (editingId) {
        await axios.put(`${API}/companies/${editingId}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert("Company updated successfully");
      } else {
        await axios.post(`${API}/companies`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert("Company added successfully");
      }

      fetchCompanies();
      setShowForm(false);
      resetForm();
    } catch (err) {
      console.error("Error saving company:", err);
      alert("Error saving company details");
    }
  };

  /* ================= DELETE COMPANY ================= */

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this company?")) return;

    try {
      await axios.delete(`${API}/companies/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert("Company deleted successfully");
      fetchCompanies();
    } catch (err) {
      console.error("Error deleting company:", err);
      alert("Error deleting company");
    }
  };

  /* ================= HELPERS ================= */

  const handleEdit = (row) => {
    setForm({
      season_id: row.season_id,
      company_name: row.company_name || "",
      contact_person: row.contact_person || "",
      mobile: row.mobile || "",
      email: row.email || "",
      address: row.address || "",
      city: row.city || "",
      district: row.district || "",
      state: row.state || "",
      pin_code: row.pin_code || "",
      gst_no: row.gst_no || "",
      bank_account: row.bank_account || "",
      ifsc_code: row.ifsc_code || ""
    });
    setEditingId(row.company_id);
    setShowForm(true);
  };

  const resetForm = () => {
    setEditingId(null);
    setForm({
      season_id: selectedSeason || "",
      company_name: "",
      contact_person: "",
      mobile: "",
      email: "",
      address: "",
      city: "",
      district: "",
      state: "",
      pin_code: "",
      gst_no: "",
      bank_account: "",
      ifsc_code: ""
    });
  };

  /* ================= COLUMNS ================= */

  const COLUMNS = [
    { key: "company_id", label: "ID" },
    { key: "company_name", label: "Company Name" },
    { key: "contact_person", label: "Contact Person" },
    { key: "mobile", label: "Mobile" },
    { key: "city", label: "City" },
    { key: "gst_no", label: "GST No" },
    {
      key: "_actions",
      label: "Actions",
      render: (_, row) => (
        <div style={{ display: "flex", gap: "8px" }}>
          <Button variant="ghost" size="sm" onClick={() => handleEdit(row)}>
            ✏️
          </Button>
          <Button variant="ghost" size="sm" onClick={() => handleDelete(row.company_id)}>
            🗑
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader title="Company Masters">
        <FiRefreshCw className="page-refresh" onClick={() => { refreshToken(); fetchCompanies(); }} />
      </PageHeader>

      <Card
        title="All Companies"
        action={
          <Button size="sm" onClick={() => { resetForm(); setShowForm(true); }}>
            + Add Company
          </Button>
        }
        noPad
      >
        <DataTable columns={COLUMNS} rows={companies} />
      </Card>

      {/* ================= COMPANY MODAL ================= */}

      {showForm && (
        <div className="modalOverlay">
          <div className="modalBox" style={{ maxWidth: '800px' }}>
            <h2>{editingId ? "Edit Company" : "Add Company"}</h2>

            <div className="form-grid">
              <FormField
                label="Company Name"
                value={form.company_name}
                onChange={(e) => setForm({ ...form, company_name: e.target.value })}
              />
              <FormField
                label="Contact Person"
                value={form.contact_person}
                onChange={(e) => setForm({ ...form, contact_person: e.target.value })}
              />
              <FormField
                label="Mobile"
                value={form.mobile}
                onChange={(e) => setForm({ ...form, mobile: e.target.value })}
              />
              <FormField
                label="Email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
              <FormField
                label="Address"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
              />
              <FormField
                label="City"
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
              />
              <FormField
                label="District"
                value={form.district}
                onChange={(e) => setForm({ ...form, district: e.target.value })}
              />
              <FormField
                label="State"
                value={form.state}
                onChange={(e) => setForm({ ...form, state: e.target.value })}
              />
              <FormField
                label="Pin Code"
                value={form.pin_code}
                onChange={(e) => setForm({ ...form, pin_code: e.target.value })}
              />
              <FormField
                label="GST No"
                value={form.gst_no}
                onChange={(e) => setForm({ ...form, gst_no: e.target.value })}
              />
              <FormField
                label="Bank Account"
                value={form.bank_account}
                onChange={(e) => setForm({ ...form, bank_account: e.target.value })}
              />
              <FormField
                label="IFSC Code"
                value={form.ifsc_code}
                onChange={(e) => setForm({ ...form, ifsc_code: e.target.value })}
              />
            </div>

            <div style={{ marginTop: 20, display: "flex", gap: 10, justifyContent: 'flex-end' }}>
              <Button variant="outline" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
              <Button onClick={handleSubmit}>
                {editingId ? "Update Company" : "Save Company"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
